// components/MintApToken.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Web3 from 'web3';
import axios from 'axios';
import '../mintApToken.css';

// Minimal ABI needed to discover Digital Authorisation Licences. This matches the AssetMultiSig ToToken contract.
const AUTHORISATION_PROGRAM_ABI = [
  {
    inputs: [],
    name: 'totalMintedTokens',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256'
      }
    ],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'tokenId',
        type: 'uint256'
      }
    ],
    name: 'ownerOf',
    outputs: [
      {
        internalType: 'address',
        name: '',
        type: 'address'
      }
    ],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'tokenId',
        type: 'uint256'
      }
    ],
    name: 'tokenURI',
    outputs: [
      {
        internalType: 'string',
        name: '',
        type: 'string'
      }
    ],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'contractURI',
    outputs: [
      {
        internalType: 'string',
        name: '',
        type: 'string'
      }
    ],
    stateMutability: 'view',
    type: 'function'
  }
];

/*
 * Normalise an Ethereum address before comparison.
 */
const normaliseAddress = (address = '') => {
  return String(address).toLowerCase();
};

/*
 * Create a unique licence key.
 */
const createLicenceKey = (contractAddress, tokenId) => {
  return `${normaliseAddress(contractAddress)}:${String(tokenId)}`;
};

/*
 * Support alternative metadata URI property names.
 */
const getLicenceURI = (licence) => {
  return (
    licence?.tokenURI ||
    licence?.metadataURI ||
    licence?.metaURI ||
    licence?.url ||
    ''
  );
};

/*
 * Extract a CID and path from an IPFS URI or gateway URL.
 */
const extractIpfsPath = (uri = '') => {
  if (uri.startsWith('ipfs://ipfs/')) {
    return uri.slice('ipfs://ipfs/'.length);
  }

  if (uri.startsWith('ipfs://')) {
    return uri.slice('ipfs://'.length);
  }

  const marker = '/ipfs/';
  const markerPosition = uri.indexOf(marker);

  if (markerPosition >= 0) {
    return uri.slice(markerPosition + marker.length);
  }

  return '';
};

/*
 * Produce several gateway URLs so that another gateway can be
 * tried if Pinata is temporarily unavailable.
 */
const getGatewayCandidates = (uri) => {
  if (!uri) {
    return [];
  }

  const ipfsPath = extractIpfsPath(uri);

  /*
   * A normal HTTP URL can be used directly.
   */
  if (!ipfsPath) {
    return [uri];
  }

  const configuredGateway =
    process.env.REACT_APP_PINATA_GATEWAY_URL?.replace(/\/$/, '');

  return [
    configuredGateway
      ? `${configuredGateway}/ipfs/${ipfsPath}`
      : null,
    `https://gateway.pinata.cloud/ipfs/${ipfsPath}`,
    `https://dweb.link/ipfs/${ipfsPath}`,
    `https://ipfs.io/ipfs/${ipfsPath}`
  ].filter(Boolean);
};

// Retrieve JSON metadata from IPFS.
const fetchIpfsMetadata = async (uri) => {
  const gatewayCandidates = getGatewayCandidates(uri);
  let finalError;

  for (const gatewayURL of gatewayCandidates) {
    try {
      const response = await axios.get(gatewayURL, {
        timeout: 15000
      });

      if (
        response.data &&
        typeof response.data === 'object'
      ) {
        return response.data;
      }

      throw new Error('The metadata is not valid JSON.');
    } catch (error) {
      finalError = error;
    }
  }

  throw (
    finalError ||
    new Error('Unable to retrieve metadata from IPFS.')
  );
};

// Get the first available metadata value.
const getMetadataValue = (metadata, ...propertyNames) => {
  for (const propertyName of propertyNames) {
    const value = metadata?.[propertyName];

    if (
      value !== undefined &&
      value !== null &&
      value !== ''
    ) {
      return value;
    }
  }

  return '';
};

// Get a value from an ERC-721 attributes array.
const getMetadataAttribute = (metadata, requestedTrait) => {
  const attributes = Array.isArray(metadata?.attributes)
    ? metadata.attributes
    : [];

  const matchingAttribute = attributes.find((attribute) => {
    const traitName = String(
      attribute?.trait_type ||
      attribute?.traitType ||
      ''
    ).toLowerCase();

    return traitName === requestedTrait.toLowerCase();
  });

  return matchingAttribute?.value ?? '';
};

// Combine blockchain information, ToToken metadata and Authorisation Program metadata.
const createLicenceRecord = ({
  metadata,
  programMetadata,
  contractAddress,
  tokenId,
  tokenURI,
  owner
}) => {
  return {
    tokenId: String(tokenId),
    tokenURI,
    metadataURI: tokenURI,
    contractAddress,
    authorisationContractAddress: contractAddress,
    owner,

    name:
      getMetadataValue(
        metadata,
        'name',
        'licenceName',
        'tokenName'
      ) ||
      `Digital Authorisation Licence #${tokenId}`,

    description: getMetadataValue(
      metadata,
      'description',
      'licenceDescription'
    ),

    status:
      getMetadataValue(
        metadata,
        'status',
        'licenceStatus'
      ) ||
      getMetadataAttribute(metadata, 'Status'),

    validFrom:
      getMetadataValue(
        metadata,
        'validFrom',
        'startDate',
        'commencementDate'
      ) ||
      getMetadataAttribute(metadata, 'Valid From'),

    validUntil:
      getMetadataValue(
        metadata,
        'validUntil',
        'expiryDate',
        'expirationDate'
      ) ||
      getMetadataAttribute(metadata, 'Valid Until'),

    issuingEntity:
      getMetadataValue(
        metadata,
        'issuingEntity',
        'issuer'
      ) ||
      getMetadataAttribute(metadata, 'Issuing Entity') ||
      getMetadataValue(
        programMetadata,
        'issuingEntity',
        'issuer'
      ),

    authorisationProgram:
      getMetadataValue(
        metadata,
        'authorisationProgram',
        'programName'
      ) ||
      getMetadataValue(
        programMetadata,
        'name',
        'programName'
      ),

    authorisedProduct:
      getMetadataValue(
        metadata,
        'authorisedProduct',
        'product',
        'species'
      ) ||
      getMetadataAttribute(metadata, 'Authorised Product'),

    authorisedQuantity:
      getMetadataValue(
        metadata,
        'authorisedQuantity',
        'quantity'
      ) ||
      getMetadataAttribute(metadata, 'Authorised Quantity'),

    unit:
      getMetadataValue(
        metadata,
        'unit',
        'quantityUnit'
      ) ||
      getMetadataAttribute(metadata, 'Unit'),

    metadata
  };
};

/*
 * Frontend eligibility check.
 * Ownership is checked separately against the blockchain.
 */
const isLicenceEligible = (licence) => {
  const status = String(licence.status || '')
    .trim()
    .toLowerCase();

  const ineligibleStatuses = [
    'inactive',
    'expired',
    'revoked',
    'suspended',
    'cancelled',
    'canceled'
  ];

  if (ineligibleStatuses.includes(status)) {
    return false;
  }

  if (licence.validFrom) {
    const commencementDate = new Date(licence.validFrom);

    if (
      !Number.isNaN(commencementDate.getTime()) &&
      commencementDate.getTime() > Date.now()
    ) {
      return false;
    }
  }

  if (licence.validUntil) {
    const expiryDate = new Date(licence.validUntil);

    if (
      !Number.isNaN(expiryDate.getTime()) &&
      expiryDate.getTime() < Date.now()
    ) {
      return false;
    }
  }

  return true;
};

const formatDate = (value) => {
  if (!value) {
    return 'Not specified';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

const shortenAddress = (address = '') => {
  if (!address || address.length < 12) {
    return address;
  }

  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const MintApToken = () => {
  const [tokenName, setTokenName] = useState('');
  const [tokenDescription, setTokenDescription] = useState('');

  const [connectedAccount, setConnectedAccount] = useState('');

  const [
    authorisationLicences,
    setAuthorisationLicences
  ] = useState([]);

  const [
    selectedLicenceKey,
    setSelectedLicenceKey
  ] = useState('');

  const [
    loadingLicences,
    setLoadingLicences
  ] = useState(false);

  const [
    licenceError,
    setLicenceError
  ] = useState('');

  const [minting, setMinting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Address of the deployed ToToken/AssetMultiSig contract.
  const authorisationProgramAddress =
    process.env.REACT_APP_AOTOKEN_CONTRACT_ADDRESS || '';

  // Discover licences acquired and currently held by the connected MetaMask wallet.
  const loadAuthorisationLicences = useCallback(
    async (requestedAccount = '') => {
      if (!window.ethereum) {
        setLicenceError(
          'MetaMask is required to discover your licences.'
        );
        setAuthorisationLicences([]);
        setSelectedLicenceKey('');
        return;
      }

      if (
        !Web3.utils.isAddress(authorisationProgramAddress)
      ) {
        setLicenceError(
          'The Authorisation Program contract address is not configured.'
        );
        setAuthorisationLicences([]);
        setSelectedLicenceKey('');
        return;
      }

      setLoadingLicences(true);
      setLicenceError('');
      setMessage('');
      setMessageType('');

      try {
        const web3 = new Web3(window.ethereum);

        let walletAccount = requestedAccount;

        if (!walletAccount) {
          let accounts = await web3.eth.getAccounts();

          if (!accounts.length) {
            accounts = await window.ethereum.request({
              method: 'eth_requestAccounts'
            });
          }

          walletAccount = accounts[0];
        }

        if (!walletAccount) {
          throw new Error(
            'No MetaMask account is connected.'
          );
        }

        setConnectedAccount(walletAccount);

        const authorisationContract =
          new web3.eth.Contract(
            AUTHORISATION_PROGRAM_ABI,
            authorisationProgramAddress
          );

        const totalMinted = Number(
          await authorisationContract.methods
            .totalMintedTokens()
            .call()
        );

        /*
         * Retrieve Authorisation Program metadata.
         */
        let programMetadata = {};

        try {
          const programURI =
            await authorisationContract.methods
              .contractURI()
              .call();

          if (programURI) {
              await fetchIpfsMetadata(programURI);
          }
        } catch (error) {
          console.warn(
            'Unable to retrieve Authorisation Program metadata:',
            error
          );
        }

        const tokenIds = Array.from(
          { length: totalMinted },
          (_, index) => index + 1
        );

        /*
         * Inspect all sequential ToToken IDs.
         */
        const discoveryResults = await Promise.all(
          tokenIds.map(async (tokenId) => {
            try {
              const owner =
                await authorisationContract.methods
                  .ownerOf(tokenId)
                  .call();

              if (
                normaliseAddress(owner) !==
                normaliseAddress(walletAccount)
              ) {
                return null;
              }

              const tokenURI =
                await authorisationContract.methods
                  .tokenURI(tokenId)
                  .call();

              if (!tokenURI) {
                console.warn(
                  `ToToken #${tokenId} has no tokenURI.`
                );
                return null;
              }

              // Fail safely when licence conditions cannot be retrieved. Do not present an unreadable licence as eligible permission.
              let metadata;

              try {
                metadata =
                  await fetchIpfsMetadata(tokenURI);
              } catch (error) {
                console.warn(
                  `Unable to retrieve ToToken #${tokenId} metadata:`,
                  error
                );
                return null;
              }

              return createLicenceRecord({
                metadata,
                programMetadata,
                contractAddress:
                  authorisationProgramAddress,
                tokenId,
                tokenURI,
                owner
              });
            } catch (error) {
              console.warn(
                `Unable to inspect ToToken #${tokenId}:`,
                error
              );
              return null;
            }
          })
        );

        const ownedLicences =
          discoveryResults.filter(Boolean);

        const eligibleLicences =
          ownedLicences.filter(isLicenceEligible);

        setAuthorisationLicences(eligibleLicences);

        if (eligibleLicences.length === 1) {
          const licence = eligibleLicences[0];

          setSelectedLicenceKey(
            createLicenceKey(
              licence.contractAddress,
              licence.tokenId
            )
          );
        } else {
          setSelectedLicenceKey('');
        }

        if (
          ownedLicences.length > 0 &&
          eligibleLicences.length === 0
        ) {
          setLicenceError(
            'Your wallet holds Digital Authorisation Licences, but none are currently eligible.'
          );
        }
      } catch (error) {
        console.error(
          'Failed to discover Authorisation Licences:',
          error
        );

        setAuthorisationLicences([]);
        setSelectedLicenceKey('');

        setLicenceError(
          error?.message ||
          'Unable to discover Digital Authorisation Licences.'
        );
      } finally {
        setLoadingLicences(false);
      }
    },
    [authorisationProgramAddress]
  );

  /*
   * Initial licence discovery.
   */
  useEffect(() => {
    loadAuthorisationLicences();
  }, [loadAuthorisationLicences]);

  /*
   * Refresh when the MetaMask account or network changes.
   */
  useEffect(() => {
    if (!window.ethereum?.on) {
      return undefined;
    }

    const handleAccountsChanged = (accounts) => {
      const nextAccount = accounts?.[0] || '';

      setConnectedAccount(nextAccount);
      setAuthorisationLicences([]);
      setSelectedLicenceKey('');
      setLicenceError('');

      if (nextAccount) {
        loadAuthorisationLicences(nextAccount);
      } else {
        setLicenceError(
          'Connect MetaMask to discover your Digital Authorisation Licences.'
        );
      }
    };

    const handleChainChanged = () => {
      setAuthorisationLicences([]);
      setSelectedLicenceKey('');
      setLicenceError('');
      loadAuthorisationLicences();
    };

    window.ethereum.on(
      'accountsChanged',
      handleAccountsChanged
    );

    window.ethereum.on(
      'chainChanged',
      handleChainChanged
    );

    return () => {
      window.ethereum.removeListener(
        'accountsChanged',
        handleAccountsChanged
      );

      window.ethereum.removeListener(
        'chainChanged',
        handleChainChanged
      );
    };
  }, [loadAuthorisationLicences]);

  const selectedLicence = useMemo(() => {
    return authorisationLicences.find((licence) => {
      return (
        createLicenceKey(
          licence.contractAddress,
          licence.tokenId
        ) === selectedLicenceKey
      );
    });
  }, [
    authorisationLicences,
    selectedLicenceKey
  ]);

  // Fresh ownership check immediately before minting.
  const verifyLicenceOwnership = async (
    web3,
    walletAccount,
    licence
  ) => {
    const contract = new web3.eth.Contract(
      AUTHORISATION_PROGRAM_ABI,
      licence.contractAddress
    );

    const currentOwner = await contract.methods
      .ownerOf(licence.tokenId)
      .call();

    if (
      normaliseAddress(currentOwner) !==
      normaliseAddress(walletAccount)
    ) {
      throw new Error(
        'The selected licence is no longer held by the connected wallet. Refresh your licences.'
      );
    }

    if (!isLicenceEligible(licence)) {
      throw new Error(
        'The selected licence is no longer eligible.'
      );
    }

    return true;
  };

  const handleMint = async (event) => {
    event.preventDefault();

    setMinting(true);
    setMessage('');
    setMessageType('');

    try {
      if (!window.ethereum) {
        throw new Error(
          'Please install MetaMask.'
        );
      }

      if (!tokenName.trim()) {
        throw new Error(
          'Please enter a Passport Name.'
        );
      }

      if (!tokenDescription.trim()) {
        throw new Error(
          'Please enter a Passport Description.'
        );
      }

      if (!selectedLicence) {
        throw new Error(
          'Please select an eligible Digital Authorisation Licence.'
        );
      }

      const apiUrl = process.env.REACT_APP_API_URL;

      if (!apiUrl) {
        throw new Error(
          'The Product Passport API URL is not configured.'
        );
      }

      const web3 = new Web3(window.ethereum);

      const accounts =
        await window.ethereum.request({
          method: 'eth_requestAccounts'
        });

      const account = accounts[0];

      if (!account) {
        throw new Error(
          'No MetaMask account is connected.'
        );
      }

      // Reverify the acquired licence before passport creation.
      await verifyLicenceOwnership(
        web3,
        account,
        selectedLicence
      );

      const authorisationLicenceURI = getLicenceURI(selectedLicence);
      const authorisationContractAddress = 
        selectedLicence.contractAddress ||
        process.env.REACT_APP_AOTOKEN_CONTRACT_ADDRESS;

      const authorisationTokenId = String(selectedLicence.tokenId);

      if (!authorisationContractAddress) {
          throw new Error(
              'The Authorisation Program contract address is not configured.'
          );
      }

      if (!authorisationLicenceURI) {
          throw new Error(
              'The selected licence has no metadata URI.'
          );
      }

      /* Keep this hash calculation consistent with your
       * existing APToken backend:
       * Passport name + description + licence URI.*/
      const metadataHash =
        web3.utils.soliditySha3(
          {
            type: 'string',
            value: tokenName.trim()
          },
          {
            type: 'string',
            value: tokenDescription.trim()
          },
          {
            type: 'string',
            value: authorisationLicenceURI
          }
        );

      if (!metadataHash) {
        throw new Error(
          'Unable to generate the Product Passport metadata hash.'
        );
      }

      const signature =
        await web3.eth.personal.sign(
          metadataHash,
          account,
          ''
        );

      if (
        !signature ||
        !signature.startsWith('0x')
      ) {
        throw new Error(
          'MetaMask returned an invalid signature.'
        );
      }

      // Submit data to APToken backend.
      const response = await axios.post(
        `${apiUrl.replace(/\/$/,'')}/api/tokens/mintToken`, {
          // Product Passport information
          name: tokenName.trim(),
          description: tokenDescription.trim(),

          // Which licence authorises its creation
          authorisationContractAddress,
          authorisationTokenId,
          authorisationLicenceURI,

          // Verification
          signature,
          frontendMetadataHash: metadataHash
        }
      );

      if (!response.data?.success) {
        throw new Error(
          response.data?.error ||
          'Product Passport creation failed.'
        );
      }

      const {
        tokenId,
        transactionHash
      } = response.data;

      setMessageType('success');

      setMessage(
        `Permission verified. Product Passport #${tokenId} was created under #${authorisationTokenId}. Transaction: ${transactionHash}`
      );

      setTokenName('');
      setTokenDescription('');
      setSelectedLicenceKey('');
    } catch (error) {
      console.error(
        'Error creating Product Passport:',
        error
      );

      setMessageType('error');

      setMessage(
        error.response?.data?.error ||
        error.message ||
        String(error)
      );
    } finally {
      setMinting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);

      window.alert(
        'Message copied to clipboard.'
      );
    } catch (error) {
      console.error(
        'Clipboard error:',
        error
      );

      window.alert(
        'Failed to copy message.'
      );
    }
  };

  return (
    <main className="passport-page">
      <section className="passport-container">
        <header className="passport-page-header">
          <h2>Create Product Passport</h2>

          <p>
            Create a Product Passport using a Digital Authorisation Licence you have acquired. The
            selected licence confirms your permission to create and use the passport for provenance recording.
          </p>
        </header>

        <div className="form-group">
            <label htmlFor="authorisation-licence">
              Select Authorisation Licence
            </label>

            <div className="licence-discovery-status">
              <div className="licence-status-icon">
                <span className="wallet-icon">▣</span>
                <span className="check-icon">✓</span>
              </div>

              <div className="licence-status-text">
                {loadingLicences ? (
                  <>
                    <strong>
                      Discovering acquired licences...
                    </strong>

                    <span>
                      Checking the connected wallet and Authorisation Program.
                    </span>
                  </>
                ) : (
                  <>
                    <strong>
                      {authorisationLicences.length}{' '}
                      eligible{' '}
                      {authorisationLicences.length === 1
                        ? 'licence'
                        : 'licences'}{' '}
                      found
                    </strong>

                    <span>
                      Acquired licences held by your connected wallet are discovered and checked automatically.
                    </span>
                  </>
                )}
              </div>
            </div>

            <select
              id="authorisation-licence"
              value={selectedLicenceKey}
              onChange={(event) =>
                setSelectedLicenceKey(
                  event.target.value
                )
              }
              disabled={
                loadingLicences ||
                authorisationLicences.length === 0
              }
              required
            >
              <option value="">
                {loadingLicences
                  ? 'Discovering licences...'
                  : 'Select an acquired Authorisation Licence'}
              </option>

              {authorisationLicences.map((licence) => {
                const licenceKey =
                  createLicenceKey(
                    licence.contractAddress,
                    licence.tokenId
                  );

                return (
                  <option
                    key={licenceKey}
                    value={licenceKey}
                  >
                    {licence.name}
                    {' — ToToken #'}
                    {licence.tokenId}
                  </option>
                );
              })}
            </select>

            {licenceError && (
              <div className="licence-error">
                {licenceError}
              </div>
            )}

            {!loadingLicences &&
              !licenceError &&
              authorisationLicences.length === 0 && (
                <div className="licence-empty">
                  No eligible Digital Authorisation Licence
                  was found for wallet{' '}
                  {shortenAddress(connectedAccount)}.
                </div>
              )}
          </div>

          {selectedLicence && (
            <section className="selected-licence-card">
              <div className="selected-licence-heading">
                <div>
                  <h2>
                    Selected Authorisation Licence
                  </h2>

                  <p>
                    Permission is available. Ownership will be verified 
                    again before the Product Passport is created.
                  </p>
                </div>

                <span className="active-badge">
                  {selectedLicence.status || 'Active'}
                </span>
              </div>

              <dl className="licence-details">
                <div>
                  <dt>Licence</dt>
                  <dd>{selectedLicence.name}</dd>
                </div>

                <div>
                  <dt>Licence ID</dt>
                  <dd>
                    ToToken #{selectedLicence.tokenId}
                  </dd>
                </div>

                <div>
                  <dt>Authorisation Program</dt>
                  <dd>
                    {selectedLicence.authorisationProgram ||
                      'Not specified'}
                  </dd>
                </div>

                <div>
                  <dt>Issuing Entity</dt>
                  <dd>
                    {selectedLicence.issuingEntity ||
                      'Not specified'}
                  </dd>
                </div>

                <div>
                  <dt>Authorised Product</dt>
                  <dd>
                    {selectedLicence.authorisedProduct ||
                        'Not specified'}
                  </dd>
                </div>

                <div>
                  <dt>Authorised Quantity</dt>
                  <dd>
                    {selectedLicence.authorisedQuantity
                      ? `${selectedLicence.authorisedQuantity} ${
                          selectedLicence.unit || ''
                        }`
                      : 'Not specified'}
                  </dd>
                </div>

                <div>
                  <dt>Valid until</dt>
                  <dd>
                    {formatDate(
                      selectedLicence.validUntil
                    )}
                  </dd>
                </div>

              </dl>
            </section>
          )}

        <form
          className="passport-form"
          onSubmit={handleMint}
        >
          <div className="form-group">
            <label htmlFor="passport-name">
              Passport Name
            </label>

            <input
              id="passport-name"
              type="text"
              value={tokenName}
              onChange={(event) =>
                setTokenName(event.target.value)
              }
              placeholder="Bush Apple Batch – September 2026"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="passport-description">
              Passport Description
            </label>

            <textarea
              id="passport-description"
              value={tokenDescription}
              onChange={(event) =>
                setTokenDescription(event.target.value)
              }
              placeholder="This Product Passport tracks the provenance of a 500 kg bush apple batch."
              rows="3"
              required
            />
          </div>

          <div className="passport-form-actions">
            <button
              type="button"
              className="refresh-licences-button"
              onClick={() =>
                loadAuthorisationLicences()
              }
              disabled={
                loadingLicences ||
                minting
              }
            >
              <span className="refresh-icon">↻</span>

              {loadingLicences
                ? 'Discovering...'
                : 'Refresh Licences'}
            </button>

            <button
              type="submit"
              className="create-passport-button"
              disabled={
                minting ||
                loadingLicences ||
                !selectedLicence
              }
            >
              {minting
                ? 'Verifying Permission...'
                : 'Create Product Passport'}
            </button>
          </div>
        </form>

        {message && (
          <div
            className={`passport-result ${messageType}`}
            role="status"
          >
            <p>{message}</p>

            <button
              type="button"
              onClick={handleCopy}
            >
              Copy
            </button>
          </div>
        )}
      </section>
    </main>
  );
};

export default MintApToken;